import React, { useState, useEffect } from 'react';
import { Search, Edit, CheckCircle2, XCircle, Clock, IndianRupee, LogOut, Plus, Trash2 } from 'lucide-react';
import { bookingService, serviceService, authService, bookings, pricingData } from './mock';
import { useNavigate } from 'react-router-dom';

const Admin = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('authToken'));
  const [allBookings, setAllBookings] = useState(bookings);
  const [filteredBookings, setFilteredBookings] = useState(bookings);
  const [searchQuery, setSearchQuery] = useState('');
  const [pricing, setPricing] = useState(pricingData);
  const [editingPrice, setEditingPrice] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [activeTab, setActiveTab] = useState('bookings');
  
  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    if (!isLoggedIn) return;
    // Fetch bookings from API
    loadBookings();
    loadPricing();
  }, [isLoggedIn]);

  const loadBookings = async () => {
    try {
      const response = await bookingService.getAllBookings();
      if (response.success) {
        setAllBookings(response.bookings);
        setFilteredBookings(response.bookings);
      }
    } catch (error) {
      console.log('Using mock data for bookings');
      setAllBookings(bookings);
      setFilteredBookings(bookings);
    }
  };

  const loadPricing = async () => {
    try {
      const response = await serviceService.getAllServices();
      if (response.success) {
        setPricing(response.services);
      }
    } catch (error) {
      console.log('Using mock data for pricing');
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredBookings(allBookings);
    } else {
      const filtered = allBookings.filter(
        (booking) =>
          booking.bookingId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          booking.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          booking.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          booking.phoneNumber?.includes(searchQuery)
      );
      setFilteredBookings(filtered);
    }
  }, [searchQuery, allBookings]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await authService.login(email, password);
      if (response.success) {
        setIsLoggedIn(true);
        setEmail('');
        setPassword('');
        loadBookings();
      } else {
        setLoginError(response.message || 'Login failed');
      }
    } catch (error) {
      // For demo: allow access if API is unavailable
      if (email === 'admin@endemiksolutions.com' && password === 'Admin123') {
        localStorage.setItem('authToken', 'demo-token');
        setIsLoggedIn(true);
        setEmail('');
        setPassword('');
      } else {
        setLoginError('Invalid credentials');
      }
    }
  };

  const handleLogout = () => {
    authService.logout();
    setIsLoggedIn(false);
    navigate('/');
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      const response = await bookingService.updateStatus(bookingId, newStatus);
      if (response.success) {
        const updatedBookings = allBookings.map((booking) =>
          booking.id === bookingId ? { ...booking, status: newStatus } : booking
        );
        setAllBookings(updatedBookings);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handlePriceUpdate = async (serviceId, newPriceValue) => {
    try {
      const response = await serviceService.updatePricing(serviceId, parseFloat(newPriceValue));
      if (response.success) {
        const updatedPricing = pricing.map((p) =>
          p.id === serviceId ? { ...p, basePrice: parseFloat(newPriceValue) } : p
        );
        setPricing(updatedPricing);
        setEditingPrice(null);
        setNewPrice('');
      }
    } catch (error) {
      console.error('Error updating price:', error);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    try {
      const response = await bookingService.deleteBooking(bookingId);
      if (response.success) {
        const updated = allBookings.filter(b => b.id !== bookingId);
        setAllBookings(updated);
        setFilteredBookings(updated);
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
    };
    return (
      <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${colors[status] || colors.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const stats = {
    total: allBookings.length,
    pending: allBookings.filter((b) => b.status === 'pending').length,
    approved: allBookings.filter((b) => b.status === 'approved').length,
    completed: allBookings.filter((b) => b.status === 'completed').length,
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-4xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent mb-2">
              🧵 Endemik Solutions
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">Manage bookings and services</p>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-100">
            {loginError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <strong>Error:</strong> {loginError}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent outline-none transition"
                  placeholder="admin@endemik-solutions.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent outline-none transition"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-2 rounded-lg font-bold hover:shadow-lg transition"
              >
                Login to Dashboard
              </button>
            </form>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-900 text-sm font-semibold mb-2">📝 Demo Credentials:</p>
              <p className="text-blue-800 text-sm mb-1"><strong>Email:</strong> admin@endemik-solutions.com</p>
              <p className="text-blue-800 text-sm"><strong>Password:</strong> admin123</p>
            </div>
          </div>

          <p className="text-center text-gray-600 text-sm mt-6">
            Only authorized administrators can access this dashboard.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-teal-50 via-white to-cyan-50 py-8 px-4 shadow">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600">Manage bookings and services</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition font-medium"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </section>

      {/* Stats */}
      <section className="px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Bookings', value: stats.total, color: 'from-blue-600 to-blue-400' },
              { label: 'Pending', value: stats.pending, color: 'from-yellow-600 to-yellow-400' },
              { label: 'Approved', value: stats.approved, color: 'from-green-600 to-green-400' },
              { label: 'Completed', value: stats.completed, color: 'from-purple-600 to-purple-400' },
            ].map((stat, idx) => (
              <div
                key={idx}
                className={`bg-gradient-to-r ${stat.color} p-6 rounded-lg text-white shadow-lg`}
              >
                <div className="text-3xl font-bold">{stat.value}</div>
                <div className="text-sm opacity-90">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="px-4 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-4 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('bookings')}
              className={`px-4 py-3 font-semibold transition border-b-2 ${
                activeTab === 'bookings'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-600 hover:text-teal-600'
              }`}
            >
              Bookings
            </button>
            <button
              onClick={() => setActiveTab('pricing')}
              className={`px-4 py-3 font-semibold transition border-b-2 ${
                activeTab === 'pricing'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-600 hover:text-teal-600'
              }`}
            >
              Pricing & Services
            </button>
          </div>

          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">All Bookings</h2>
              
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by ID, name, email, or phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 outline-none"
                  />
                </div>
              </div>

              <div className="space-y-4">
                {filteredBookings.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No bookings found</p>
                ) : (
                  filteredBookings.map((booking) => (
                    <div key={booking.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-bold text-lg text-gray-900">{booking.customerName}</h3>
                          <p className="text-sm text-gray-600">ID: {booking.bookingId}</p>
                        </div>
                        {getStatusBadge(booking.status)}
                      </div>

                      <div className="grid md:grid-cols-3 gap-4 mb-4 text-sm">
                        <div>
                          <span className="text-gray-600">Email:</span> {booking.email}
                        </div>
                        <div>
                          <span className="text-gray-600">Phone:</span> {booking.phoneNumber}
                        </div>
                        <div>
                          <span className="text-gray-600">Service:</span> {booking.serviceType}
                        </div>
                        <div>
                          <span className="text-gray-600">Date:</span> {new Date(booking.preferredDate).toLocaleDateString()}
                        </div>
                        <div>
                          <span className="text-gray-600">Time:</span> {booking.preferredTime}
                        </div>
                        <div>
                          <span className="text-gray-600">Cost:</span> ₹{booking.estimatedCost}
                        </div>
                      </div>

                      <p className="text-gray-700 mb-4 bg-gray-50 p-3 rounded">
                        <strong>Sample:</strong> {booking.sampleDetails}
                      </p>

                      <div className="flex gap-2 flex-wrap">
                        {booking.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleStatusUpdate(booking.id, 'approved')}
                              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition flex items-center gap-2"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              Approve
                            </button>
                            <button
                              onClick={() => handleStatusUpdate(booking.id, 'rejected')}
                              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition flex items-center gap-2"
                            >
                              <XCircle className="w-4 h-4" />
                              Reject
                            </button>
                          </>
                        )}
                        {booking.status === 'approved' && (
                          <button
                            onClick={() => handleStatusUpdate(booking.id, 'completed')}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                          >
                            Mark Completed
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteBooking(booking.id)}
                          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Pricing</h2>

              <div className="space-y-4">
                {pricing.map((service) => (
                  <div key={service.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-bold text-gray-900">{service.name}</h3>
                        <p className="text-sm text-gray-600">{service.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {editingPrice === service.id ? (
                          <>
                            <input
                              type="number"
                              value={newPrice}
                              onChange={(e) => setNewPrice(e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded w-24 focus:ring-2 focus:ring-teal-600 outline-none"
                              placeholder="Price"
                            />
                            <button
                              onClick={() => handlePriceUpdate(service.id, newPrice)}
                              className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 transition"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingPrice(null)}
                              className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 transition"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="text-2xl font-bold text-teal-600">₹{service.basePrice}</span>
                            <button
                              onClick={() => {
                                setEditingPrice(service.id);
                                setNewPrice(service.basePrice);
                              }}
                              className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 transition flex items-center gap-2"
                            >
                              <Edit className="w-4 h-4" />
                              Edit
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Admin;
